import type { SessionRecorder } from "@talent/client-telemetry";
import type { TaskDefinition } from "@talent/protocol";
import {
  expectedSpanResponse,
  generateBuildTarget,
  generateCodeKey,
  generateDifference,
  generateKeyMap,
  generateMirror,
  generateParts,
  generateLetterTrial,
  generateWordTrial,
  generateMathProblem,
  generateMaze,
  generateOddOneOutStimulus,
  generatePatternStimulus,
  generateRobotPuzzle,
  generateOrdering,
  generatePairs,
  generateRuleSwitch,
  generateSearch,
  generateSequence,
  generateShadow,
  generateSpanStimulus,
  generateSudoku,
  generateTiling,
  placementAllowed,
  runProgram,
  scoreBuildTrial,
  scoreCodeKeyTrial,
  scoreDifferenceTrial,
  scoreLetterTrial,
  scoreWordTrial,
  scoreMathTrial,
  scoreMazeTrial,
  scoreMirrorTrial,
  passedKeyMap,
  scorePartsTrial,
  scorePatternTrial,
  scoreSearchTrial,
  scoreShadowTrial,
  scoreSpanTrial,
  scoreSudokuTrial,
  scoreTilingTrial,
  stepBudget,
} from "@talent/tasks";
import { hasSeenTutorial, markTutorialSeen } from "./profile.js";
import { runBuildTask } from "./build-task.js";
import { runChoiceTask } from "./choice-task.js";
import { runCodeKeyTask } from "./code-key-task.js";
import { runDifferenceTask } from "./difference-task.js";
import { ensureGlyphFont } from "./glyph.js";
import { paintLetter } from "./letter-card.js";
import { paintWordChoice } from "./word-card.js";
import { runMathTask } from "./math-task.js";
import { runKeyMapTask } from "./key-map-task.js";
import { runMazeTask } from "./maze-task.js";
import { runMirrorTask } from "./mirror-task.js";
import { runRobotTask } from "./robot-task.js";
import { runOrderingTask } from "./ordering-task.js";
import { runPairsTask } from "./pairs-task.js";
import { runSearchTask } from "./search-task.js";
import { runSequenceTask } from "./sequence-task.js";
import { paintPartsCard } from "./parts-card.js";
import { ensureAnimalAtlas, paintShadowCard } from "./shadow-card.js";
import { runRuleSwitchTask } from "./rule-switch-task.js";
import { runSpanTask } from "./span-task.js";
import { runSudokuTask } from "./sudoku-task.js";
import { runTilingTask } from "./tiling-task.js";
import type { TutorialChoice } from "./tutorial-prompt.js";

/**
 * What the child is told after a task. Task-local, never a construct estimate.
 *
 * Carries the task it belongs to. Without that, finishing one game and then opening another shows
 * the first game's reward screen until the second has finished starting up, because "a result
 * exists" reads as "the current game is over".
 */
export type LastResult = { taskId: string } & (
  | { scoring: "span"; best: number }
  | { scoring: "accuracy"; correct: number; total: number }
);

/** Everything a renderer needs that the shell owns. */
export interface RunContext {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  signal: AbortSignal;
  /** Awaited after a demonstration; resolving with "replay" runs it again. */
  holdForTutorial: () => Promise<TutorialChoice>;
  /**
   * What this run is FOR, when the child was sent here rather than choosing (ADR-011).
   *
   * Both fields travel into `task_start`, which is what makes "who practised what" a query instead
   * of an inference from levels. Absent for free play, which is its own finding.
   */
  objectiveId?: string;
  assignmentId?: string;
  /** The level to open at, from the same suggestion. Clamped per task by `ladder.ts`. */
  startAt?: number;
}

/**
 * Runs the game for a task and reports what to tell the child.
 *
 * Extracted from the shell because it is the one part of it that is not React: a map from a family
 * to the renderer that plays it, depending on nothing but its arguments. Leaving it inside the
 * component meant every change to a game touched the file that owns routing, boot and lifecycle —
 * three unrelated concerns editing each other's neighbourhood.
 *
 * Keyed by FAMILY rather than by scoring model. Scoring was enough while there were two games; it
 * is not now, since several families score on accuracy and share no interaction at all.
 */
export async function runGame(ctx: RunContext): Promise<LastResult> {
  const { task, holdForTutorial } = ctx;
  const common = {
    canvasParent: ctx.canvasParent,
    task: ctx.task,
    formId: ctx.formId,
    recorder: ctx.recorder,
    signal: ctx.signal,
    /**
     * Spread conditionally rather than passed as `number | undefined`.
     *
     * `exactOptionalPropertyTypes` is on, so an optional property and a property holding `undefined`
     * are different types — and the distinction is the right one here: "no suggestion" and "a
     * suggestion of nothing" would otherwise be the same value arriving at ten renderers.
     */
    ...(ctx.startAt !== undefined ? { startAt: ctx.startAt } : {}),
  };

  /**
   * Which renderer runs a task.
   *
   * Keyed by FAMILY rather than by scoring model. Scoring was enough while there were two
   * games; it is not now — three families score on accuracy and they are a matching game, a
   * spot-the-odd-one game and a construction game, which share no interaction at all. The
   * map is exhaustive over the families in the bundle, so adding a family without a renderer
   * is a compile error rather than a blank screen a child taps into.
   */
  const runners: Record<string, () => Promise<LastResult>> = {
    "wm-span-forward": async () => {
      const r = await runSpanTask({
        ...common,
        generateStimulus: generateSpanStimulus,
        expectedResponse: expectedSpanResponse,
        scoreTrial: scoreSpanTrial,
      });
      return { taskId: task.id, scoring: "span", best: r.rawScore };
    },
    "wm-span-backward": async () => {
      const firstBackward = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runSpanTask({
        ...common,
        showTutorial: firstBackward,
        onTutorialComplete: holdForTutorial,
        generateStimulus: generateSpanStimulus,
        expectedResponse: expectedSpanResponse,
        scoreTrial: scoreSpanTrial,
      });
      return { taskId: task.id, scoring: "span", best: r.rawScore };
    },
    "vs-pattern-match": async () => {
      const r = await runChoiceTask({
        ...common,
        generateStimulus: generatePatternStimulus,
        scoreTrial: scorePatternTrial,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    /**
     * The alphabet, on the same renderer as the two shape games.
     *
     * The only two things it adds are a painter that puts a letter on a card instead of dots, and
     * waiting for the webfont first. That wait is not optional: Pixi rasterises a `Text` when it is
     * created, so a letter drawn before Vazirmatn has loaded is drawn in whatever face the phone
     * had and stays that way for the rest of the sitting — which in a game about letterforms is
     * not a cosmetic fault, it is the wrong stimulus.
     */
    "lit-letters": async () => {
      await ensureGlyphFont();
      const r = await runChoiceTask({
        ...common,
        generateStimulus: generateLetterTrial,
        scoreTrial: scoreLetterTrial,
        paintContent: paintLetter,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    /**
     * Which word is the letter in — the alphabet game's companion, on the same renderer again.
     *
     * Same wait for the webfont and the same reason: a word rasterised before Vazirmatn has loaded
     * is drawn in whatever face the phone had and stays that way, which in a game about finding a
     * letter shape is the wrong stimulus rather than a cosmetic fault.
     */
    "lit-find-letter": async () => {
      await ensureGlyphFont();
      const r = await runChoiceTask({
        ...common,
        generateStimulus: generateWordTrial,
        scoreTrial: scoreWordTrial,
        paintContent: paintWordChoice,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    "vs-odd-one-out": async () => {
      const r = await runChoiceTask({
        ...common,
        generateStimulus: generateOddOneOutStimulus,
        scoreTrial: scorePatternTrial,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    /**
     * Shadow matching, on the same renderer again.
     *
     * All it needed was a painter that draws a flat silhouette when the card is the target and the
     * coloured animal otherwise — which is the same argument the letters game made, and the reason
     * `paintContent` is a parameter rather than four copies of this renderer.
     *
     * The wait for the atlas is the same wait, for the same reason: a card painted before its
     * texture has arrived is painted empty and stays empty, which in a game about looking at one
     * picture is the wrong stimulus rather than a slow load.
     */
    "vs-shadow": async () => {
      await ensureAnimalAtlas();
      const r = await runChoiceTask({
        ...common,
        generateStimulus: generateShadow,
        scoreTrial: scoreShadowTrial,
        paintContent: paintShadowCard,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    /**
     * The part/whole game, on the choice renderer with a painter of its own.
     *
     * The painter is doing something the other three do not: it draws the SAME stimulus two ways,
     * assembled for the target and scattered for an option. That asymmetry is the task, and it is
     * still a painter rather than a renderer, which is the whole argument for the parameter.
     */
    "vs-parts": async () => {
      const r = await runChoiceTask({
        ...common,
        generateStimulus: generateParts,
        scoreTrial: scorePartsTrial,
        paintContent: paintPartsCard,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    "vs-maze": async () => {
      const r = await runMazeTask({
        ...common,
        generateMaze,
        stepBudget,
        scoreTrial: scoreMazeTrial,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    "vs-sequence": async () => {
      const r = await runSequenceTask({
        ...common,
        generateStimulus: generateSequence,
        scoreTrial: scorePatternTrial,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    /**
     * The colour world runs on the same renderer as the shape one.
     *
     * The generator decides which world it is from the task's family, so there is nothing to switch on
     * here — and that is the point: one renderer means a fix to how a strip is drawn or scored reaches
     * both, instead of reaching one and leaving the other quietly different.
     */
    "vs-sequence-color": async () => {
      const r = await runSequenceTask({
        ...common,
        generateStimulus: generateSequence,
        scoreTrial: scorePatternTrial,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    "wm-pairs": async () => {
      const first = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runPairsTask({
        ...common,
        showTutorial: first,
        onTutorialComplete: holdForTutorial,
        generateBoard: generatePairs,
      });
      return {
        taskId: task.id,
        scoring: "accuracy",
        correct: r.trialsCorrect,
        total: r.trialsAttempted,
      };
    },
    "vs-ordering": async () => {
      const first = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runOrderingTask({
        ...common,
        showTutorial: first,
        onTutorialComplete: holdForTutorial,
        generateRound: generateOrdering,
      });
      return {
        taskId: task.id,
        scoring: "accuracy",
        correct: r.trialsCorrect,
        total: r.trialsAttempted,
      };
    },
    "ef-rule-switch": async () => {
      const first = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runRuleSwitchTask({
        ...common,
        showTutorial: first,
        onTutorialComplete: holdForTutorial,
        generateRound: generateRuleSwitch,
      });
      return {
        taskId: task.id,
        scoring: "accuracy",
        correct: r.trialsCorrect,
        total: r.trialsAttempted,
      };
    },
    "vs-difference": async () => {
      // The demonstration runs the first time only. Deciding that here rather than in the
      // renderer keeps device-local storage out of the game loop.
      const first = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runDifferenceTask({
        ...common,
        generateStimulus: generateDifference,
        scoreTrial: scoreDifferenceTrial,
        showTutorial: first,
        onTutorialComplete: holdForTutorial,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    /**
     * Find them all, on a renderer of its own.
     *
     * The only game here where a trial is a SWEEP rather than a choice, which is why it could not
     * join the choice renderer: that one resolves on the first tap, and everything about its
     * feedback assumes exactly one answer was given.
     *
     * The demonstration is not optional. Every other game in the bundle has trained the child to
     * tap once and wait, and nothing on a text-free screen can say "keep going".
     */
    /**
     * Colour by the key, on a renderer of its own.
     *
     * It could not join the choice renderer for the same reason the search game could not: a trial
     * here is a BOARD rather than a pick, and it finishes when the child stops rather than on their
     * first tap.
     *
     * The demonstration is not optional. Two things on this screen are invisible — that the bar at
     * the top is an instruction rather than decoration, and that a tile cycles rather than toggles
     * — and neither can be said in words on a surface that may not contain any.
     */
    /**
     * Carry the pattern on, on a renderer of its own.
     *
     * The sequence renderer could not take it: that one presents a row and resolves on one card,
     * and this is a strip the child paints. The demonstration is not optional either — three
     * things here are invisible, and the one that matters most is that a GAP in the motif is part
     * of the pattern rather than a cell nobody got to.
     */
    /**
     * Drive the car — the visible twin of the rule-switch game, on a renderer of its own.
     *
     * A trial here is a whole ROUND, like rule-switch, because what it describes only exists across
     * cards: what a child did right after the key moved is meaningless without what they were doing
     * before it. It could not share that renderer, though — there the answer is a bin and here it
     * is a direction, and the pads have to sit in the shape of the answer.
     *
     * The demonstration is not optional and it is the longest in the bundle. The thing it teaches
     * is that the key CHANGES partway through, and a rule about what happens later cannot be shown
     * in one card.
     */
    "ef-key-map": async () => {
      const firstKeyMap = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runKeyMapTask({
        ...common,
        showTutorial: firstKeyMap,
        onTutorialComplete: holdForTutorial,
        generateRound: generateKeyMap,
        passed: passedKeyMap,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    "vs-tiling": async () => {
      const firstTiling = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runTilingTask({
        ...common,
        showTutorial: firstTiling,
        onTutorialComplete: holdForTutorial,
        generateStimulus: generateTiling,
        scoreTrial: scoreTilingTrial,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    "vs-code-key": async () => {
      const firstKey = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runCodeKeyTask({
        ...common,
        showTutorial: firstKey,
        onTutorialComplete: holdForTutorial,
        generateStimulus: generateCodeKey,
        scoreTrial: scoreCodeKeyTrial,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    "vs-search": async () => {
      const firstSearch = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runSearchTask({
        ...common,
        showTutorial: firstSearch,
        onTutorialComplete: holdForTutorial,
        generateStimulus: generateSearch,
        scoreTrial: scoreSearchTrial,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    "vs-robot": async () => {
      const firstRobot = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runRobotTask({
        ...common,
        showTutorial: firstRobot,
        onTutorialComplete: holdForTutorial,
        generatePuzzle: generateRobotPuzzle,
        runProgram,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    "math-arith": async () => {
      const first = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runMathTask({
        ...common,
        showTutorial: first,
        onTutorialComplete: holdForTutorial,
        generateProblem: generateMathProblem,
        scoreTrial: scoreMathTrial,
      });
      return {
        taskId: task.id,
        scoring: "accuracy",
        correct: r.trialsCorrect,
        total: r.trialsAttempted,
      };
    },
    "logic-sudoku": async () => {
      const first = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runSudokuTask({
        ...common,
        showTutorial: first,
        onTutorialComplete: holdForTutorial,
        generatePuzzle: generateSudoku,
        placementAllowed,
        scoreTrial: scoreSudokuTrial,
      });
      return {
        taskId: task.id,
        scoring: "accuracy",
        correct: r.trialsCorrect,
        total: r.trialsAttempted,
      };
    },
    "vs-build-copy": async () => {
      const firstBuild = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runBuildTask({
        ...common,
        showTutorial: firstBuild,
        onTutorialComplete: holdForTutorial,
        generateTarget: generateBuildTarget,
        scoreTrial: scoreBuildTrial,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
    /**
     * The butterfly's other wing, on a renderer of its own.
     *
     * It used to run on the building game's renderer with a transformed answer, which is why it was
     * indistinguishable from it on screen. The reflection had to become something a child can SEE —
     * a body down the middle, two wings, a spot travelling across when they get it wrong — and none
     * of that is a parameter you can pass to a renderer built to copy.
     *
     * The demonstration is not optional. What it teaches is the misconception itself: that the spot
     * nearest the body comes back nearest the body rather than furthest from it.
     */
    "vs-mirror": async () => {
      const firstMirror = !hasSeenTutorial(task.id);
      markTutorialSeen(task.id);
      const r = await runMirrorTask({
        ...common,
        showTutorial: firstMirror,
        onTutorialComplete: holdForTutorial,
        generateStimulus: generateMirror,
        scoreTrial: scoreMirrorTrial,
      });
      return { taskId: task.id, scoring: "accuracy", correct: r.trialsCorrect, total: r.trialsAttempted };
    },
  };

  const run = runners[task.family];
  if (!run) throw new Error(`no renderer for family ${task.family}`);
  return run();
}
